import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useTable, useSortBy, usePagination } from 'react-table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { graphqlErrorsToUiErrors, mutations, queries } from '../../api';
import { getDealsForTeam } from '../../api/queries';
import { getDealFolders } from '../../api/queries_v2';
import slugify from '../../utils/slugify';
import { useTeamInfo } from '../../team-id-context';
import { useAuth, usePermissions } from '../../auth';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { Dropdown, DropdownToggle, DropdownBody, DropdownItem } from '../../components/dropdown';
import SearchInput from '../../components/search-input';
import ErrorMessage from '../../components/error-message';
import PrivateLayout, { ContentHeader } from '../../components/private-layout';
import { Table } from '../../components/table';
import { Checkbox } from '../../components/checkbox-or-radio-field';
import LoadingSpinner from '../../components/loading-spinner';
import { dealStatusMessages, statusOptions } from './constants';
import PendingInvitesModal from './pending-deals-modals/PendingInvitesModal';
import { UserHasUnassignedDealsModal } from '../../components/shared/modals';
import { Permissions } from '../../shared/constants/permissions';
import { DealStatus } from './components/statusDropdown/statusDropdown';
import AddNewDealModal from './modals/AddNewDealModal';
import CreateDealFolderModal from './components/modals/CreateDealFolderModal';
import SubscriptionRequiredModal from '../modals/subscriptionModal/SubscriptionRequiredModal';
import { DropdownProvider } from '../../contexts/DropdownContext';
import { fillPlaceholders, paths } from '../../routing';
import { takeDocumentDestination } from '../document-wizard/shared-offer/usePendingDealInvitationRecovery';
import { defaultDealStatuses } from './constants/DealConstants';
import { dealTableColumns } from './components/deal-table/dealTableColumns';
import { TitleFormattedMessage } from '../../components/titleFormattedMessage';
import { ContentBody } from '../../components/private-layout/ContentBody';
import { ActivityDashboard } from '../../components/activity-dashboard';
import { ReactComponent as FilterIcon } from '../../shared/icons/filter-icon.svg';
import { ReactComponent as HomeIcon } from '../../shared/icons/home-icon.svg';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import styles from './styles.module.scss';
import FilterSidebar from './components/filterSidebar';
import { MobileTable } from '../../components/mobile-layout/MobileTable';
import { MobileDealItem } from '../../components/mobile-layout/MobileDealItem';
import { MobileFolderItem } from '../../components/mobile-layout/MobileFolderItem';
import { MobilePagination } from '../../components/mobile-layout/MobilePagination';
import ArchiveModal from './archiveModal';

const DealsLanding = () => {
  const { formatMessage } = useIntl();
  const { teamId } = useTeamInfo();
  const { dealToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, hasPermission } = usePermissions();
  const { canCreateDeal } = useUserRestrictions();
  const canEdit = hasPermission(Permissions.Edit);
  const [{ user }] = useAuth();

  // State for modals
  const [isPendingInvitesModalOpen, setPendingInvitesModalOpen] = useState(false);
  const [isUnassignedDealModalOpen, setIsUnassignedDealModalOpen] = useState(true);

  // Fetch deal invitations - needed for pending invites modal to show "from" field
  const { data: invitationsData } = useQuery(queries.getUserDealInvitations);

  // Fetch pending deals to check if we should show the modal
  const { data: pendingDealsData, refetch: refetchPendingDeals } = useQuery(queries.getUserPendingDeals);

  // Use the fetched data or fall back to empty arrays
  const dealInvitations = invitationsData?.currentUser?.dealInvitations || user?.dealInvitations || [];
  const pendingDeals = pendingDealsData?.currentUser?.pendingDeals || user?.pendingDeals || [];
  const currentPendingDeals = pendingDeals || [];

  // Function to open pending invites modal
  const handleOpenPendingInvitesModal = useCallback(() => {
    setPendingInvitesModalOpen(true);
  }, []);

  // Auto-open pending invites modal when there are pending deals
  useEffect(() => {
    if (pendingDeals && pendingDeals.length > 0) {
      setPendingInvitesModalOpen(true);
    }
  }, [pendingDeals]);
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showSubscriptionRequiredModal, setSubscriptionRequiredModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [sorting, setSorting] = useState('');
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [dealToArchive, setDealToArchive] = useState<string | null>(null);
  const [updateDealDetails] = useMutation(mutations.updateDealDetails);

  // Folder and filter state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filterStatuses, setFilterStatuses] = useState(new Set(defaultDealStatuses));
  const [folderPath, setFolderPath] = useState<Array<{ id: string | null; name: string }>>([]);

  const {
    loading: dealsDataLoading,
    data: dealsData,
    error: graphqlError,
    refetch: refetchDeals,
  } = useQuery(getDealsForTeam, {
    variables: {
      teamId,
      folderId: filterText ? null : currentFolderId, // Don't filter by folder when searching
    },
  });

  // Fetch deal folders
  const { data: foldersData, refetch: refetchFolders } = useQuery(getDealFolders, {
    variables: {
      teamId,
    },
    skip: !teamId,
  });

  const {
    loading: loadingUser,
    data: userData,
    error: _error,
    refetch: refetchUser,
  } = useQuery(queries.getCurrentUserBasic);

  // Check for upgrade query parameter from templates/clauses redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const upgradeType = params.get('upgrade');
    if (upgradeType) {
      setSubscriptionRequiredModal(true);
      // Clean up URL to remove query parameter
      navigate(fillPlaceholders(paths.deals.landing, { teamId }), { replace: true });
    }
  }, [location.search, navigate, teamId]);

  const handleUpdateDealDetails = useCallback(
    async (dealId: string, newStatus: DealStatus) => {
      // If the new status is archived, show the archive modal instead
      if (newStatus === 'archived') {
        setDealToArchive(dealId);
        setIsArchiveModalOpen(true);
        return;
      }

      // For all other statuses, update directly
      await updateDealDetails({
        variables: {
          dealId,
          dealDetails: {
            status: newStatus,
          },
        },
        refetchQueries: [
          {
            query: getDealsForTeam,
            variables: {
              teamId,
            },
          },
        ],
      });
    },
    [teamId, updateDealDetails]
  );

  const handleAddNewDealButton = canCreateDeal
    ? () => {
        setShowAddDealModal(true);
      }
    : () => {
        setSubscriptionRequiredModal(true);
      };

  const handleNewFolderButton = canCreateDeal
    ? () => {
        setShowCreateFolderModal(true);
      }
    : () => {
        setSubscriptionRequiredModal(true);
      };

  const userDealNotifications = useMemo(() => {
    return userData?.currentUser?.communicationPreferences.deal_notifications;
  }, [userData?.currentUser?.communicationPreferences.deal_notifications]);

  const refreshUserData = useCallback(async () => {
    await refetchUser();
  }, [refetchUser]);

  const handleArchiveConfirm = useCallback(async () => {
    if (!dealToArchive) return;

    await updateDealDetails({
      variables: {
        dealId: dealToArchive,
        dealDetails: {
          status: 'archived',
        },
      },
      refetchQueries: [
        {
          query: getDealsForTeam,
          variables: {
            teamId,
          },
        },
      ],
    });

    // Close modal and clear state
    setIsArchiveModalOpen(false);
    setDealToArchive(null);
  }, [dealToArchive, updateDealDetails, teamId]);

  const handleArchiveCancel = useCallback(() => {
    setIsArchiveModalOpen(false);
    setDealToArchive(null);
  }, []);

  const handleDealActionSuccess = useCallback(() => {
    // Refetch deals data when deal actions (like move to folder) succeed
    refetchDeals();
  }, [refetchDeals]);

  const handleAcceptInviteSuccess = useCallback(async () => {
    // Refetch both deals and pending deals after successful invitation acceptance
    await Promise.all([refetchDeals(), refetchPendingDeals()]);

    // If the user landed here because they followed a "Review document" link
    // before accepting this invite, acceptance has now created their deal access
    // right — resume the document they originally clicked.
    const destination = takeDocumentDestination();
    if (destination) navigate(destination, { replace: true });
  }, [refetchDeals, refetchPendingDeals, navigate]);

  const columns = useMemo(
    () =>
      dealTableColumns({
        canEdit,
        handleUpdateDealDetails,
        userDealNotifications: userDealNotifications,
        refetchUser: refreshUserData,
        teamId,
        onSuccess: handleDealActionSuccess,
      }),
    [canEdit, handleUpdateDealDetails, userDealNotifications, refreshUserData, teamId, handleDealActionSuccess]
  );

  // Status options for filtering

  useEffect(() => {
    // Only check invitations if there's a dealToken and invitations have been fetched
    if (!dealToken || !invitationsData) return;

    const foundInvitation = dealInvitations.find(
      (dealInvitation: { token: string }) => dealInvitation.token === dealToken
    );

    if (!foundInvitation && dealInvitations.length > 0) {
      // Only navigate away if we have invitations but none match the token
      navigate(fillPlaceholders(paths.deals.landing, { teamId }));
    }
  }, [dealInvitations, dealToken, navigate, teamId, invitationsData]);

  const onDealDetail = (dealId: string) => {
    const dealDeatilsUrl = fillPlaceholders(paths.deals.detail, {
      teamId,
      dealId: dealId,
    });
    navigate(dealDeatilsUrl);
  };

  // Filter and mutate data for the table
  const data = useMemo(() => {
    if (dealsDataLoading || !dealsData || loadingUser) return [];

    let combinedData: any[] = [];

    // Add folders to the data
    // If the API returns folders for this user, they should see them
    // Don't show ANY folders when searching by deal title - only show deals
    if (foldersData?.dealFolders?.folders && !filterText) {
      const folderRows = foldersData.dealFolders.folders
        .filter((folder: any) => {
          // Filter by current folder location
          if (currentFolderId) {
            return folder.parentFolder?.id === currentFolderId;
          } else {
            return !folder.parentFolder;
          }
        })
        .map((folder: any) => ({
          id: folder.id,
          isFolder: true,
          title: folder.name,
          folderPath: null,
          creator: '',
          status: '',
          updatedAt: folder.createdAt,
          onRowClick: () => {
            // Navigate into the folder
            setCurrentFolderId(folder.id);
            setFolderPath([...folderPath, { id: folder.id, name: folder.name }]);
          },
        }));
      combinedData = [...folderRows];
    }

    // Add deals to the data
    const filteredDeals = dealsData.dealsForTeam
      .filter((deal: any) => {
        // Filter by current folder if not searching
        const isCounterparty = deal.currentUserRole === 'receiver';

        if (!filterText && !isCounterparty) {
          // Only apply folder filtering to team members (creators)
          if (currentFolderId) {
            // In a folder, show only deals in that folder
            if (deal.folder?.id !== currentFolderId) return false;
          } else {
            // At root level, show only deals without a folder
            if (deal.folder) return false;
          }
        }

        if (filterText) {
          // Filter out if no address fields match the query
          const filterTextLower = filterText.toLocaleLowerCase();
          const searchValues = filterTextLower.split(' ');
          let searchResults: string[] = [];
          searchValues.forEach((obj) => {
            if (deal.title.toLocaleLowerCase().includes(obj)) {
              searchResults.push(deal.title.toLocaleLowerCase());
            }
          });

          if (searchResults.length !== searchValues.length) return false;
        }

        if (filterStatuses.size && filterStatuses.size < statusOptions.length) {
          // Filter out if the status isn't one of those chosen
          if (!filterStatuses.has(deal.status)) return false;
        }

        return true;
      })
      .map((deal: any) => {
        return {
          id: deal.id,
          isFolder: false,
          currentUserRole: deal.currentUserRole,
          title: deal.title,
          // Only show folder path when searching AND not a counterparty (don't reveal creator's folder structure)
          folderPath:
            filterText && deal.folder && deal.currentUserRole !== 'receiver'
              ? `Deals > ${Array.isArray(deal.folder.path) ? deal.folder.path.join(' > ') : (deal.folder.path || deal.folder.name)}`
              : null,
          creator: deal.creatorName,
          status: deal.status,
          updatedAt: deal.updatedAt,
          onRowClick: () => {
            const dealDeatilsUrl = fillPlaceholders(paths.deals.detail, {
              teamId,
              dealId: deal.id,
            });
            navigate(dealDeatilsUrl);
          },
        };
      });

    combinedData = [...combinedData, ...filteredDeals];

    combinedData.sort((a: any, b: any) => {
      // Always show folders first (like standard directory UIs)
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      
      // If both are same type (both folders or both deals), then apply sorting
      switch (sorting) {
        case 'ascName':
          return a.title.localeCompare(b.title);
        case 'descName':
          return b.title.localeCompare(a.title);
        case 'statusInProgress':
          return a.status.localeCompare(b.status);
        case 'statusArchived':
          return b.status.localeCompare(a.status);
        case 'dateToLatest':
          return (b?.updatedAt || '').localeCompare(a?.updatedAt || '');
        case 'dateToNewest':
          return (a?.updatedAt || '').localeCompare(b?.updatedAt || '');
        case 'accessMyTeam':
          return a.creator.localeCompare(b.creator);
        case 'accessPrivate':
          return b.creator.localeCompare(a.creator);

        default:
          return (b?.updatedAt || '').localeCompare(a?.updatedAt || '');
      }
    });

    return combinedData;
  }, [
    dealsDataLoading,
    dealsData,
    filterText,
    sorting,
    filterStatuses,
    teamId,
    navigate,
    loadingUser,
    currentFolderId,
    foldersData?.dealFolders?.folders,
    statusOptions.length,
    folderPath,
  ]);

  const sortBy = useMemo(() => {
    switch (sorting) {
      case 'ascName':
        return {
          id: 'title',
          desc: false,
        };
      case 'descName':
        return {
          id: 'title',
          desc: true,
        };
      case 'statusInProgress':
        return {
          id: 'status',
          desc: false,
        };
      case 'statusArchived':
        return {
          id: 'status',
          desc: true,
        };
      case 'dateToLatest':
        return {
          id: 'updatedAt',
          desc: false,
        };
      case 'dateToNewest':
        return {
          id: 'updatedAt',
          desc: true,
        };
      case 'accessMyTeam':
        return {
          id: 'creator',
          desc: false,
        };
      case 'accessPrivate':
        return {
          id: 'creator',
          desc: true,
        };

      default:
        return undefined;
    }
  }, [sorting]);

  const table = useTable(
    {
      data,
      columns: columns as any,
      initialState: {
        hiddenColumns: ['dealId'],
        sortBy: sortBy ? [sortBy] : [],
        pageSize: 50,
        pageIndex: 0,
      },
      autoResetPage: false,
    } as any,
    useSortBy,
    usePagination
  );

  const foundInvitation = dealInvitations
    ? dealInvitations.find((dealInvitation: { token: string }) => dealInvitation.token === dealToken)
    : null;

  return (
    <DropdownProvider>
      <PrivateLayout>
        <Helmet title={formatMessage({ id: 'title.deals', defaultMessage: 'Deals' })} />
        <ContentHeader>
          <div className={styles.dealPageHeader}>
            <div data-testid="deals-header" role={'heading'} className={'ml-3'}>
              <FormattedMessage id="title.deals" defaultMessage="Deals" />
            </div>
            <ActivityDashboard teamId={teamId} onOpenPendingInvitesModal={handleOpenPendingInvitesModal} />
          </div>
        </ContentHeader>

        <ContentBody className={styles.contentBodyFull}>
        {(dealsDataLoading || loadingUser) ? (
          <LoadingSpinner />
        ) : !dealsData ? (
          <ErrorMessage>{graphqlErrorsToUiErrors(graphqlError)?._general}</ErrorMessage>
        ) : (
          <>
          <Row className="mb-3">
            <Col className={'mx-3 d-flex justify-content-between non-mobile-flex'}>
              <div className={'d-flex flex-fill'}>
                <SearchInput
                  className="mr-3"
                  value={filterText}
                  onChange={(event: any) => setFilterText(event.currentTarget.value)}
                  placeholder={formatMessage({
                    id: 'deals.filter.title',
                    defaultMessage: 'Search Deal Name',
                  })}
                  aria-label={formatMessage({
                    id: 'deals.filter.name',
                    defaultMessage: 'Name',
                  })}
                />

                <div className={'mr-3'}>
                  <Dropdown>
                    <Dropdown.Toggle
                      id="dropdown-filter-status"
                      data-testid="dropdown-filter-status"
                      as={DropdownToggle}
                      /*
                  // @ts-ignore */
                      className="w-100"
                    >
                      <FormattedMessage
                        id="deals.filter.status.label-readout"
                        defaultMessage={`{filterCount, select,
                      0 {Status}
                      all {Status}
                      1 {Status ({status, select,
                           in_progress {In Progress}
                           completed {Completed}
                           archived {Archived}
                           lost {Lost}
                           other {{status}}
                      })}
                      other {{filterCount} Statuses}
                    }`}
                        values={{
                          filterCount: filterStatuses.size === statusOptions.length ? 'all' : filterStatuses.size,
                          status: filterStatuses.size ? filterStatuses.values().next().value : '',
                        }}
                      />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      <DropdownItem highlightable>
                        <Checkbox
                          checked={filterStatuses.size === statusOptions.length}
                          indeterminate={filterStatuses.size > 0 && filterStatuses.size < statusOptions.length}
                          id="dropdown-filter-statuses-_all"
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const checkbox = event.currentTarget;
                            if (checkbox.checked) {
                              setFilterStatuses(new Set(Object.keys(dealStatusMessages)));
                            } else {
                              setFilterStatuses(new Set());
                            }
                          }}
                          label={formatMessage({
                            id: 'deals.filter.status.all-statuses',
                            defaultMessage: 'All Statuses',
                          })}
                        />
                      </DropdownItem>
                      <DropdownBody>
                        {statusOptions.map(({ value: status, label }) => (
                          <DropdownItem key={status} highlightable>
                            <Checkbox
                              key={status}
                              value={status}
                              checked={filterStatuses.has(status)}
                              id={`dropdown-filter-statuses-${slugify(status)}`}
                              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                const checkbox = event.currentTarget;
                                const newSet = new Set(filterStatuses);
                                if (checkbox.checked) newSet.add(checkbox.value);
                                else newSet.delete(checkbox.value);
                                setFilterStatuses(newSet);
                              }}
                              label={<FormattedMessage {...label} />}
                            />
                          </DropdownItem>
                        ))}
                      </DropdownBody>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              {(isAdmin || canEdit) && (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button variant="primary" className="mr-2" onClick={handleNewFolderButton}>
                    <FormattedMessage id="cta.new-folder" defaultMessage="New Folder" />
                  </Button>
                  <Button
                    className="btn btn-secondary"
                    onClick={handleAddNewDealButton}
                    data-testid="button-add-new-deal"
                  >
                    <TitleFormattedMessage id="deals.add" defaultMessage="Add new deal" />
                  </Button>
                </div>
              )}
            </Col>
            <Col className={'mx-3 d-flex flex-column justify-content-between mobile-flex'} style={{ paddingTop: '1rem' }}>
              <div className={'d-flex flex-fill'}>
                <SearchInput
                  iconClassName={styles.mobileSearch__icon}
                  inputClassName={styles.mobileSearch}
                  className={styles.mobileSearch__container}
                  value={filterText}
                  onChange={(event: any) => setFilterText(event.currentTarget.value)}
                  placeholder={formatMessage({
                    id: 'deals.filter.title',
                    defaultMessage: 'Search Deal Name',
                  })}
                  aria-label={formatMessage({
                    id: 'deals.filter.name',
                    defaultMessage: 'Name',
                  })}
                />

                <Button className="filterBtn" onClick={() => setShowFilter(true)}>
                  <FilterIcon width={24} height={24} />
                </Button>

                {(isAdmin || canEdit) && (
                  <Button
                    className="addBtn"
                    onClick={handleAddNewDealButton}
                    data-testid="button-add-new-deal-mobile"
                    style={{ marginLeft: '0.5rem' }}
                  >
                    <PlusIcon width={24} height={24} />
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          {/* Breadcrumb Navigation */}
          <Row className="mb-2">
            <Col className={'mx-3'}>
              <div className="d-flex align-items-center" style={{ fontWeight: 'bold', fontSize: '14px' }}>
                <HomeIcon width={18} height={18} className="text-primary mr-2" style={{ marginTop: '-1px' }} />
                {/* Always show root "Deals" */}
                {currentFolderId || folderPath.length > 0 ? (
                  <>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none text-primary"
                      style={{ fontWeight: 'bold', fontSize: '14px' }}
                      onClick={() => {
                        setCurrentFolderId(null);
                        setFolderPath([]);
                      }}
                    >
                      <FormattedMessage id="deals.breadcrumb.root" defaultMessage="Deals" />
                    </Button>
                    <span className="mx-2 text-muted">{'>'}</span>
                  </>
                ) : (
                  <span className="text-dark">
                    <FormattedMessage id="deals.breadcrumb.root" defaultMessage="Deals" />
                  </span>
                )}
                {/* Folder path items */}
                {folderPath.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    {index === folderPath.length - 1 ? (
                      <span className="text-dark">{folder.name}</span>
                    ) : (
                      <>
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none text-primary"
                          style={{ fontWeight: 'bold', fontSize: '14px' }}
                          onClick={() => {
                            const newPath = folderPath.slice(0, index + 1);
                            setCurrentFolderId(folder.id);
                            setFolderPath(newPath);
                          }}
                        >
                          {folder.name}
                        </Button>
                        <span className="mx-2 text-muted">{'>'}</span>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Col>
          </Row>

          {/* Desktop Table */}
          <div className={`${styles.scrollableContainer} non-mobile`}>
            <Row>
              <Col>
                {data.length ? (
                  <Table striped isStickyHeader paginationBelow isStickyPagination table={table} />
                ) : (
                  <>
                    {filterText || filterStatuses.size !== defaultDealStatuses.size ? (
                      <p className="lead text-center">
                        <FormattedMessage id="phrase.no-results" defaultMessage="No results match your filters." />
                      </p>
                    ) : (
                      <div className="text-center">
                        <h2>
                          <FormattedMessage id="phrase.deals.no-deals" defaultMessage="No Deals" />
                        </h2>
                        <p className="lead">
                          <FormattedMessage id="phrase.deals.empty" defaultMessage="Your deals will appear here." />
                        </p>
                      </div>
                    )}
                  </>
                )}
              </Col>
            </Row>
          </div>

          {/* Mobile Table */}
          <div className={styles.mobileTableContainer}>
            {data.length ? (
              <>
                <div className={styles.mobileListArea}>
                  <MobileTable>
                    {table.page?.map((row: any) => {
                      const item = row.original;
                      if (item.isFolder) {
                        return (
                          <MobileFolderItem
                            key={`mobile-folder-${item.id}`}
                            folder={item}
                            onClick={item.onRowClick}
                          />
                        );
                      }

                      return (
                        <MobileDealItem
                          key={`mobile-${item.id}`}
                          deal={item}
                          handleUpdateDealDetails={handleUpdateDealDetails}
                          onClick={() => onDealDetail(item.id)}
                        />
                      );
                    })}
                  </MobileTable>
                </div>
                <MobilePagination
                  pageIndex={table.state.pageIndex}
                  pageCount={table.pageCount}
                  canPreviousPage={table.canPreviousPage}
                  canNextPage={table.canNextPage}
                  gotoPage={table.gotoPage}
                  previousPage={table.previousPage}
                  nextPage={table.nextPage}
                  totalItems={data.length}
                  pageSize={table.state.pageSize}
                />
              </>
            ) : (
              <>
                {filterText || filterStatuses.size !== defaultDealStatuses.size ? (
                  <p className="lead text-center" style={{ padding: '2rem' }}>
                    <FormattedMessage id="phrase.no-results" defaultMessage="No results match your filters." />
                  </p>
                ) : (
                  <div className="text-center" style={{ padding: '2rem' }}>
                    <h2>
                      <FormattedMessage id="phrase.deals.no-deals" defaultMessage="No Deals" />
                    </h2>
                    <p className="lead">
                      <FormattedMessage id="phrase.deals.empty" defaultMessage="Your deals will appear here." />
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          </>
        )}
        </ContentBody>
        <PendingInvitesModal
          isOpen={isPendingInvitesModalOpen}
          onClose={() => setPendingInvitesModalOpen(false)}
          currentPendingDeals={currentPendingDeals}
          pendingInvites={dealInvitations}
          showSubscriptionRequiredModal={() => {
            setSubscriptionRequiredModal(true);
          }}
          onAcceptSuccess={handleAcceptInviteSuccess}
        />
        {foundInvitation && dealToken && isUnassignedDealModalOpen && (
          <UserHasUnassignedDealsModal
            handleHide={() => {
              setIsUnassignedDealModalOpen(false);
              navigate(fillPlaceholders(paths.deals.landing, { teamId }));
            }}
            showSubscriptionRequiredModal={() => {
              setSubscriptionRequiredModal(true);
            }}
            onSuccess={() => {
              setIsUnassignedDealModalOpen(false);
              navigate(fillPlaceholders(paths.deals.landing, { teamId }));
            }}
            invitation={foundInvitation}
          />
        )}
        <AddNewDealModal
          showAddDealModal={showAddDealModal}
          currentFolderId={currentFolderId}
          onModalHide={() => {
            setShowAddDealModal(false);
          }}
        />
        <CreateDealFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          teamId={teamId}
          onFolderCreated={() => {
            refetchFolders();
            refetchDeals();
            setShowCreateFolderModal(false);
          }}
        />
        <SubscriptionRequiredModal
          isOpen={showSubscriptionRequiredModal}
          onClose={() => setSubscriptionRequiredModal(false)}
        />
        <FilterSidebar
          isOpen={showFilter}
          filterStatuses={filterStatuses}
          onClose={() => setShowFilter(!showFilter)}
          setSorting={setSorting}
          setFilterStatuses={setFilterStatuses}
        />
        <ArchiveModal
          isOpen={isArchiveModalOpen}
          onUpdateArchived={handleArchiveConfirm}
          onClose={handleArchiveCancel}
        />
      </PrivateLayout>
    </DropdownProvider>
  );
};

export default DealsLanding;
